import type { Patient, Reference } from '@medplum/fhirtypes'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { KBD, TypographyDetail } from '@seen/ui'
import { IconArrowUp } from '@tabler/icons-react'
import type { JSX, Ref } from 'react'
import { z } from 'zod'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import {
  RichTextEditor,
  type RichTextEditorRef,
} from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { mergeRefs } from '#src/utils/mergeRefs.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

const formSchema = z.object({
  message: z
    .string()
    .transform(trimHTML)
    .refine((v) => v.length > 0, { message: 'Message required' }),
})

export function ReplyComposer({
  comment,
  subject,
  editorRef,
}: {
  comment: CommentCommunication
  subject?: Reference<Patient>
  editorRef?: Ref<RichTextEditorRef>
}): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: '',
    },
    restorationId: `reply-composer-${comment.reference.reference}`,
  })

  const createCommunication =
    useCreateMutation<CommentCommunication>().mutateAsync

  if (isReadOnly) {
    return null
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    const communication = new CommentCommunication({
      message: values.message,
      sender: practitioner.reference,
      subject: subject ?? comment.subject,
      partOf: [comment.reference],
    })

    await createCommunication(communication, {
      onSuccess: () => {
        form.reset()
      },
    })
  })

  return (
    <div className="flex items-center gap-2">
      <Avatar
        profile={practitioner.reference}
        size="icon"
        className="mt-2.5 self-start"
      />
      <Form onSubmit={handleSubmit} className="flex flex-1 items-center gap-2">
        <FormField
          control={form.control}
          name="message"
          render={({ field: { ref, ...rest } }) => (
            <RichTextEditor
              placeholder="Leave a reply…"
              variant="unstyled"
              className="-ml-3 flex-1 pl-3"
              contentClassName="text-sm pl-3 -ml-3 w-auto py-5 -my-2.5 [&_p]:leading-5"
              enablePractitionerMention
              {...rest}
              ref={mergeRefs([editorRef, ref])}
            />
          )}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <FormSubmitButton
              control={form.control}
              size="icon"
              variant="outline"
              className="self-end rounded-full"
              aria-label="Submit reply"
            >
              <IconArrowUp className="size-4" />
            </FormSubmitButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="flex items-center gap-1 px-2"
          >
            <div className="flex gap-1">
              <KBD>⌘</KBD>
              <KBD>Enter</KBD>
            </div>
            <TypographyDetail className="text-foreground-secondary">
              to submit reply
            </TypographyDetail>
          </TooltipContent>
        </Tooltip>
      </Form>
    </div>
  )
}
